<template>
  <div class="d-flex align-center mb-4">
    <h2 class="headline6">{{ $t("usersPage.associatedDevicesTable.title") }}</h2>
    <v-chip
      v-if="devices.length > 0"
      class="ml-3 pa-2"
      rounded="pill"
      density="compact"
      variant="flat"
      color="indigo-pale"
    >
      <span class="subtitle2">
        {{ devices.length }}
      </span>
    </v-chip>
  </div>

  <v-table class="devices-table border border-indigo-faint border-opacity-100">
    <thead>
      <tr>
        <th class="icon-col" :aria-label="$t('ariaLabels.iconColumn')"></th>
        <th class="text-left body3 text-indigo-medium pl-0">
          {{ $t("usersPage.associatedDevicesTable.name") }}
        </th>
        <th class="text-left body3 text-indigo-medium">
          {{ $t("usersPage.associatedDevicesTable.os") }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="device in devices" :key="device.enrollmentCode">
        <td class="icon-col">
          <v-icon :icon="getDeviceIcon(device.osType)" size="24" />
        </td>
        <td class="pl-0">
          <span class="body2 coro-link" @click="onDeviceClick(device)">
            {{ device.hostName || device.enrollmentCode }}
          </span>
        </td>
        <td class="body2">
          {{ getOsDisplayName(device) }}
        </td>
      </tr>
    </tbody>
  </v-table>
</template>

<script setup lang="ts">
import camelCase from "lodash/camelCase";
import type { UserAssociatedDevice } from "@/_store/users.module.ts";
import { OsType } from "@/constants/devices.ts";
import { useI18n } from "vue-i18n";

const i18n = useI18n();

const emit = defineEmits<{
  (e: "device-clicked", device: UserAssociatedDevice): void;
}>();

defineProps<{ devices: UserAssociatedDevice[] }>();

const getDeviceIcon = (osType: OsType): string => {
  const iconMap = {
    [OsType.WINDOWS]: `$windowsPc`,
    [OsType.OSX]: `$macPc`,
    [OsType.IOS]: `$iosDevice`,
    [OsType.ANDROID]: `$androidDevice`,
    [OsType.MAC_OS]: `$macPc`,
    [OsType.LINUX]: `$linuxPc`,
  };
  return iconMap[osType] ?? `$deviceS`;
};

const onDeviceClick = (device: UserAssociatedDevice) => {
  emit("device-clicked", device);
};

const getOsDisplayName = (device: UserAssociatedDevice) => {
  if (device.osDisplayName) {
    return device.osDisplayName;
  }
  const translationKey = `general.osVersion.${camelCase(device.osType)}`;
  if (i18n.te(translationKey)) {
    return i18n.t(translationKey);
  }
  return device.osType;
};
</script>

<style scoped>
.devices-table {
  --v-table-header-height: 48px;
  --v-table-row-height: 48px;
}

.icon-col {
  width: 24px;
}
</style>
